/**
 * PLAN PRICING + REFERRAL CREDIT LOOKUPS (Node/CommonJS)
 *
 * Mirror of supabase/functions/_shared/planPricing.ts for the Express
 * billing webhook. Keep both files in sync when prices change.
 *
 * Source of truth for subscription prices (monthly) and per-referral credits.
 * Marketing site: rei-crm-marketing/src/app/pricing/PricingClient.tsx
 */

const PLAN_PRICE = {
  free: 0,
  starter: 49, // was 147 until 2026-09-29 — starter IS the new $49 entry tier, not a separate 'lite' plan
  growth: 597,
  growth_plus: 1497,
  scale: 2497,
  enterprise: 0,
};

const REFERRAL_CREDIT = {
  free: 0,
  starter: 10, // was 25 at the $147 price — GUESS scaled to the new $49 price, not confirmed with Tom
  growth: 100,
  growth_plus: 150,
  scale: 200,
  enterprise: 0,
};

/**
 * Voice AI overage rate, cents/minute (added 2026-09-16). Display-only — the
 * real invoiced amount is always computed by Stripe from the usage record
 * report-voice-overage-usage sends, not from this constant. It exists so
 * GET /api/billing/usage can show a tenant an estimate that matches what the
 * cron will actually report. Must be kept equal to STRIPE_VOICE_OVERAGE_PRICE_ID's
 * real unit_amount (set once via scripts/setup-voice-overage-stripe-price.js) —
 * if the Stripe price ever changes, update this too. Node-only: nothing on the
 * Deno side currently displays this estimate, so it is not mirrored into
 * supabase/functions/_shared/planPricing.ts.
 */
const VOICE_OVERAGE_RATE_CENTS = 25;

function normalize(plan) {
  const p = (plan || 'free').toString().toLowerCase().trim();
  if (['starter', 'growth', 'growth_plus', 'scale', 'enterprise', 'free'].includes(p)) return p;
  return 'free';
}

function getPlanPrice(plan) {
  return PLAN_PRICE[normalize(plan)];
}

function getReferralCredit(refereePlan) {
  return REFERRAL_CREDIT[normalize(refereePlan)];
}

/**
 * Cap referral credits at the referrer's monthly plan price.
 *
 * Sort active referrals by full credit DESC, fill while budget remains.
 * Last referral that overflows gets partial credit for whatever is left.
 * Invariant: total_applied <= referrer_plan_price.
 *
 * Returns dollars (not cents). Convert at the call site if needed.
 */
function allocateReferralCredits(referrerPlan, activeReferrals) {
  const budget = getPlanPrice(referrerPlan);

  const enriched = activeReferrals.map((r) => ({
    id: r.id,
    referee_plan: normalize(r.referee_plan),
    full_credit: getReferralCredit(r.referee_plan),
  }));

  enriched.sort((a, b) => b.full_credit - a.full_credit);

  let remaining = budget;
  const credited = [];
  const uncredited = [];

  for (const ref of enriched) {
    const applied = Math.min(ref.full_credit, remaining);
    if (applied > 0) {
      credited.push({
        id: ref.id,
        referee_plan: ref.referee_plan,
        full_credit: ref.full_credit,
        applied_credit: applied,
        partially_capped: applied < ref.full_credit,
      });
      remaining -= applied;
    } else {
      uncredited.push({
        id: ref.id,
        referee_plan: ref.referee_plan,
        full_credit: ref.full_credit,
        applied_credit: 0,
        partially_capped: false,
      });
    }
  }

  return {
    referrer_plan_price: budget,
    total_applied: budget - remaining,
    credited,
    uncredited,
  };
}

module.exports = {
  PLAN_PRICE,
  REFERRAL_CREDIT,
  VOICE_OVERAGE_RATE_CENTS,
  getPlanPrice,
  getReferralCredit,
  allocateReferralCredits,
};
